"use client";

import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui/components/alert-dialog";
import { Button } from "@ui/components/button";
import { Field, FieldDescription, FieldLabel } from "@ui/components/field";
import { Input } from "@ui/components/input";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { type ReactNode, useId, useState } from "react";
import { errorMessage } from "../lib/errors";

/**
 * Confirmation for actions that can't be undone and destroy a lot: the person
 * types the exact name of the thing before the button unlocks.
 */
export function TypeToConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmText,
	actionLabel,
	onConfirm,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: ReactNode;
	/** What must be typed, usually the name being deleted. */
	confirmText: string;
	actionLabel: string;
	onConfirm: () => Promise<void>;
}) {
	const id = useId();
	const [typed, setTyped] = useState("");
	const [pending, setPending] = useState(false);
	const matches = typed.trim() === confirmText.trim();

	const close = (next: boolean) => {
		if (pending) {
			return;
		}
		if (!next) {
			setTyped("");
		}
		onOpenChange(next);
	};

	return (
		<AlertDialog open={open} onOpenChange={close}>
			<AlertDialogContent>
				<form
					className="flex flex-col gap-6"
					onSubmit={async (event) => {
						event.preventDefault();
						if (!matches) {
							return;
						}
						setPending(true);
						try {
							await onConfirm();
							setTyped("");
							onOpenChange(false);
						} catch (error) {
							toast.add({
								type: "error",
								title: "That didn't work",
								description: errorMessage(error),
							});
						} finally {
							setPending(false);
						}
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>{title}</AlertDialogTitle>
						<AlertDialogDescription>
							{description}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<Field>
						<FieldLabel htmlFor={id}>
							Type{" "}
							<span className="font-mono">{confirmText}</span> to
							confirm
						</FieldLabel>
						<Input
							id={id}
							value={typed}
							onChange={(event) => setTyped(event.target.value)}
							autoComplete="off"
							spellCheck={false}
						/>
						<FieldDescription>
							This can't be undone.
						</FieldDescription>
					</Field>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={pending}>
							Cancel
						</AlertDialogCancel>
						<Button
							type="submit"
							variant="destructive"
							disabled={!matches || pending}
						>
							{pending ? (
								<Spinner data-icon="inline-start" />
							) : null}
							{actionLabel}
						</Button>
					</AlertDialogFooter>
				</form>
			</AlertDialogContent>
		</AlertDialog>
	);
}
